import { useMemo, useState } from 'react';
import { Calendar } from '@/components/ui/calendar';

const TZ = 'America/Edmonton';
const dateKey = (d) => new Date(d).toLocaleDateString('en-CA', { timeZone: TZ });
// The calendar grid itself has no timezone — a clicked day is just a Y/M/D. Extract it natively
// instead of re-running it through an Edmonton conversion, which would shift it across midnight.
const localKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Shared by the customer portal's "Tiffin History" and the staff customer profile — both already
// have a flat list of redemptions; this just groups them by Edmonton-local calendar day.
export function RedemptionCalendar({ redemptions, showStaff = false }) {
  const [selectedDay, setSelectedDay] = useState(null);

  const byDate = useMemo(() => {
    const map = {};
    for (const r of redemptions) {
      const key = dateKey(r.createdAt);
      (map[key] = map[key] || []).push(r);
    }
    return map;
  }, [redemptions]);

  const activityDates = useMemo(() => Object.keys(byDate).map((k) => new Date(`${k}T12:00:00`)), [byDate]);
  const selectedKey = selectedDay ? localKey(selectedDay) : null;
  const dayTransactions = selectedKey ? byDate[selectedKey] || [] : [];

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-border/40 p-2 flex justify-center">
        <Calendar
          mode="single"
          selected={selectedDay}
          onSelect={setSelectedDay}
          modifiers={{ hasActivity: activityDates }}
          modifiersClassNames={{ hasActivity: 'bg-saffron/15 text-saffron font-bold rounded-full' }}
          classNames={{
            day_selected: '!bg-saffron !text-white hover:!bg-saffron hover:!text-white focus:!bg-saffron rounded-full',
          }}
          data-testid="redemption-calendar"
        />
      </div>

      {selectedDay && (
        <div className="bg-white rounded-xl border border-border/40 p-4 space-y-3" data-testid="calendar-day-detail">
          <p className="font-heading font-semibold text-charcoal">
            {selectedDay.toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
          {dayTransactions.length === 0 && (
            <p className="text-sm text-muted-ink" data-testid="calendar-day-empty">No tiffins were taken on this date.</p>
          )}
          {dayTransactions.map((r) => (
            <div
              key={r.id}
              data-testid="calendar-day-transaction"
              className="flex justify-between items-start text-sm border-t border-border/30 pt-2 first:border-t-0 first:pt-0"
            >
              <div>
                <p className="text-charcoal font-medium">
                  {new Date(r.createdAt).toLocaleTimeString('en-CA', { timeZone: TZ, hour: 'numeric', minute: '2-digit' })}
                </p>
                <p className="text-muted-ink text-xs">
                  {r.balanceBefore} → {r.balanceAfter} remaining
                  {showStaff ? ` · ${r.method === 'QR' ? 'QR Scan' : 'Manual'}` : ''}
                </p>
                {showStaff && r.staff?.name && <p className="text-muted-ink text-xs">Processed by: {r.staff.name}</p>}
              </div>
              <span className="font-heading text-lg font-700 text-saffron shrink-0">-{r.quantity}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
